/**
 * Admin-console wrapper around the shared ImportQuestionsDialog.
 *
 * Wires admin-specific concerns: ZIP + lenient-JSON file parsing,
 * teacher-on-behalf-of, dry-run + conflict resolution. The actual HTTP layer
 * (real import + dry-run preview) lives in shared-ui via `importApi`.
 */

import { useCallback } from 'react';
import { ImportQuestionsDialog as SharedDialog, parseUploadedFile } from '@shared-ui/import-questions-dialog';
import { importApi, questionApi } from '../../api/adminApi';

interface TeacherOption {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  teacherOptions: TeacherOption[];
  onImported?: (importBatchId?: string) => void;
}

export function ImportQuestionsDialog({ isOpen, onClose, teacherOptions, onImported }: Props) {
  const handleCreateAssignment = useCallback(async (teacherId: string, title: string, questionIds: string[]) => {
    await questionApi.createAssignmentFromImport(teacherId, title, questionIds);
  }, []);

  return (
    <SharedDialog
      isOpen={isOpen}
      onClose={onClose}
      onImported={onImported}
      parseFile={parseUploadedFile}
      importFn={importApi.importQuestions}
      previewFn={importApi.previewImportQuestions}
      acceptFileTypes=".json,.zip"
      teacherOptions={teacherOptions}
      supportsDryRun
      infoMessage="Questions will be imported into the public library (or the teacher's bank when 'Import as' is set)."
      onCreateAssignment={handleCreateAssignment}
    />
  );
}
